import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  Bot,
  FileStack,
  Gavel,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '@/lib/api';
import {
  AppEmptyState,
  AppSectionHeader,
} from '@/components/layout/AppPage';
import JollyDictationButton from '@/components/JollyDictationButton';
import SourceTrustBadge from '@/components/SourceTrustBadge';

const STRATEGY_STANCES = [
  'Maintainability-first',
  'Strict on interim relief',
  'Record-heavy',
  'Timeline-driven',
  'Settlement-forward',
  'Skeptical of delay',
];

function normalizePlan(value) {
  const candidate = String(value || 'FREE').trim().toUpperCase();
  if (candidate === 'EARLY_BIRD' || candidate === 'EARLY_BELIEVER') return 'COMMUNITY';
  return candidate;
}

function createdJudgeLimit(user) {
  const platformRole = String(user?.platform_role || '').toUpperCase();
  if (platformRole === 'ADMIN' || platformRole === 'OWNER') return 999;
  const plan = normalizePlan(user?.plan);
  if (plan === 'COMMUNITY') return 5;
  if (['PRO', 'TEAM', 'FIRM'].includes(plan)) return 999;
  return 3;
}

function judgeLimitLabel(limit) {
  return limit >= 999 ? 'Unlimited' : String(limit);
}

function clampPercent(value, fallback = 50) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.min(100, Math.round(numeric)));
}

function normalizeJudgeType(value) {
  const candidate = String(value || 'simulated').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (candidate === 'hypothetical') return 'simulated';
  if (['verified', 'matter_linked', 'simulated'].includes(candidate)) return candidate;
  return 'simulated';
}

function judgeTypeLabel(judge) {
  const type = normalizeJudgeType(judge?.type);
  if (type === 'verified') return 'Real judge-linked';
  if (type === 'matter_linked') return 'Matter-linked context';
  return 'Simulated judge';
}

function judgeTrustLabel(judge) {
  const type = normalizeJudgeType(judge?.type);
  if (type === 'verified') return 'Verified Authority';
  if (type === 'matter_linked') return 'Matter File';
  return 'Simulation';
}

function buildEmptyJudgeThread() {
  return {
    thread_id: '',
    judge_id: '',
    messages: [],
  };
}

function buildMiniJudgeState() {
  return {
    name: '',
    temperament: 'Measured',
    short_description: '',
    acceptance_percent: 50,
    rejection_percent: 50,
    stance_tendencies: [],
  };
}

function JudgeChip({ active, disabled, judge, onClick, onRemove }) {
  return (
    <div
      style={{
        borderRadius: '18px',
        border: active ? '1px solid rgba(15,118,110,0.38)' : '1px solid var(--border-default)',
        background: active ? 'rgba(15,118,110,0.08)' : 'var(--bg-base)',
        padding: '14px',
        display: 'grid',
        gap: '10px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={onClick}
          style={{
            border: 'none',
            background: 'transparent',
            padding: 0,
            textAlign: 'left',
            display: 'grid',
            gap: '6px',
            cursor: 'pointer',
          }}
        >
          <span style={{ color: 'var(--text-primary)', fontWeight: 800 }}>{judge.name}</span>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <SourceTrustBadge label={judgeTrustLabel(judge)} compact />
            <span style={{ color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 700 }}>
              {judgeTypeLabel(judge)}
            </span>
          </div>
        </button>
        <button
          type="button"
          className="app-chat-button"
          onClick={onRemove}
          disabled={disabled}
        >
          Remove
        </button>
      </div>
      <span style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
        {judge.short_description || judge.temperament || 'No bench note yet.'}
      </span>
    </div>
  );
}

function FlowCard({ title, description, children }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: '10px',
        padding: '16px',
        borderRadius: '18px',
        border: '1px solid var(--border-default)',
        background: 'var(--bg-base)',
        alignContent: 'start',
      }}
    >
      <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>{title}</div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
        {description}
      </div>
      {children}
    </div>
  );
}

function AnalysisBlock({ label, items }) {
  return (
    <div
      style={{
        borderRadius: '14px',
        border: '1px solid var(--border-default)',
        padding: '14px',
        background: 'var(--bg-elevated)',
        display: 'grid',
        gap: '8px',
      }}
    >
      <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>{label}</div>
      {items.length ? items.map((item) => (
        <div key={item} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
          {item}
        </div>
      )) : (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
          No signal surfaced yet.
        </div>
      )}
    </div>
  );
}

export default function JudgeStrategyWorkspace({
  workspace,
  user,
  getAuthHeaders,
  reloadWorkspace,
}) {
  const matterId = workspace?.matter?.matter_id || '';
  const judgeProfiles = workspace?.case?.judge_profiles || [];
  const judgeLimit = useMemo(() => createdJudgeLimit(user), [user]);
  const judgeQuotaText = `${judgeProfiles.length}/${judgeLimitLabel(judgeLimit)} active judge agents`;

  const [judgeSearchQuery, setJudgeSearchQuery] = useState('');
  const [judgeSearchResults, setJudgeSearchResults] = useState([]);
  const [judgeSearching, setJudgeSearching] = useState(false);
  const [judgeInferResults, setJudgeInferResults] = useState([]);
  const [judgeInferring, setJudgeInferring] = useState(false);
  const [miniJudge, setMiniJudge] = useState(buildMiniJudgeState);
  const [selectedJudgeId, setSelectedJudgeId] = useState('');
  const [judgeAnalysis, setJudgeAnalysis] = useState(null);
  const [judgeAnalysisLoading, setJudgeAnalysisLoading] = useState(false);
  const [judgeThread, setJudgeThread] = useState(buildEmptyJudgeThread);
  const [judgeChatInput, setJudgeChatInput] = useState('');
  const [judgeSubmitting, setJudgeSubmitting] = useState(false);
  const [mutationBusyId, setMutationBusyId] = useState('');

  const selectedJudge = useMemo(
    () => judgeProfiles.find((item) => item.judge_id === selectedJudgeId) || judgeProfiles[0] || null,
    [judgeProfiles, selectedJudgeId],
  );

  const visibleJudgeThread = useMemo(() => {
    if (!selectedJudge) return buildEmptyJudgeThread();
    if (judgeThread?.judge_id === selectedJudge.judge_id) return judgeThread;
    return { thread_id: '', judge_id: selectedJudge.judge_id, messages: [] };
  }, [judgeThread, selectedJudge]);

  useEffect(() => {
    const nextJudges = workspace?.case?.judge_profiles || [];
    const preferredJudgeId = workspace?.activeJudgeThread?.judge_id || nextJudges[0]?.judge_id || '';
    setJudgeThread(workspace?.activeJudgeThread || buildEmptyJudgeThread());
    setSelectedJudgeId((current) => (
      current && nextJudges.some((judge) => judge.judge_id === current) ? current : preferredJudgeId
    ));
  }, [workspace]);

  useEffect(() => {
    const controller = new AbortController();
    const query = judgeSearchQuery.trim();
    if (!matterId || query.length < 2) {
      setJudgeSearchResults([]);
      return () => controller.abort();
    }

    setJudgeSearching(true);
    fetch(`${API}/cases/judges/search?q=${encodeURIComponent(query)}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => (response.ok ? response.json() : { results: [] }))
      .then((payload) => setJudgeSearchResults(payload.results || []))
      .catch(() => setJudgeSearchResults([]))
      .finally(() => setJudgeSearching(false));

    return () => controller.abort();
  }, [getAuthHeaders, judgeSearchQuery, matterId]);

  useEffect(() => {
    const activeJudgeId = selectedJudge?.judge_id;
    if (!matterId || !activeJudgeId) {
      setJudgeAnalysis(null);
      return undefined;
    }
    const controller = new AbortController();
    setJudgeAnalysisLoading(true);
    fetch(`${API}/cases/${matterId}/judge-analysis/${activeJudgeId}`, {
      headers: getAuthHeaders(),
      credentials: 'include',
      signal: controller.signal,
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.detail || 'Unable to load judge analysis');
        setJudgeAnalysis(payload.analysis || null);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          setJudgeAnalysis(null);
        }
      })
      .finally(() => setJudgeAnalysisLoading(false));
    return () => controller.abort();
  }, [getAuthHeaders, matterId, selectedJudge?.judge_id]);

  const addJudgeToMatter = useCallback(async (judge, busyKey) => {
    if (!matterId) {
      toast.error('Save the matter first, then add judge analysis.');
      return;
    }
    if (judgeProfiles.length >= judgeLimit) {
      toast.error(`Your plan allows up to ${judgeLimitLabel(judgeLimit)} active judge agents in this matter.`);
      return;
    }
    const nextBusyKey = busyKey || judge.judge_id || judge.name || 'judge';
    setMutationBusyId(nextBusyKey);
    try {
      const response = await fetch(`${API}/cases/${matterId}/judges`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(judge),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Unable to add judge agent');
      if (reloadWorkspace) {
        await reloadWorkspace(matterId);
      }
      setSelectedJudgeId(payload?.judge?.judge_id || '');
      toast.success('Judge strategy agent added.');
      setJudgeSearchQuery('');
      setJudgeSearchResults([]);
    } catch (error) {
      toast.error(error.message || 'Unable to add judge agent');
    } finally {
      setMutationBusyId('');
    }
  }, [getAuthHeaders, judgeLimit, judgeProfiles.length, matterId, reloadWorkspace]);

  const removeJudgeFromMatter = useCallback(async (judge) => {
    if (!matterId) return;
    setMutationBusyId(judge.judge_id || judge.name || 'judge');
    try {
      const nextJudgeProfiles = judgeProfiles.filter((item) => item.judge_id !== judge.judge_id);
      const response = await fetch(`${API}/cases/${matterId}/save`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          mode: workspace?.case?.mode || 'new',
          title: workspace?.case?.title || workspace?.matter?.title || '',
          description: workspace?.matter?.description || '',
          summary: workspace?.case?.summary || '',
          current_objective: workspace?.matterDesk?.current_objective?.text || workspace?.matter?.case_current_objective || '',
          court_forum: workspace?.matter?.court_forum || '',
          case_number: workspace?.matter?.case_number || '',
          source_query: workspace?.matter?.case_import?.source_query || '',
          matter_id: matterId,
          workspace_id: workspace?.case?.workspace_id || '',
          imported_case: workspace?.matter?.case_import || null,
          judge_prefill_text: '',
          judges: nextJudgeProfiles,
          explicit_save: true,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Unable to remove judge agent');
      if (reloadWorkspace) {
        await reloadWorkspace(matterId);
      }
      toast.success('Judge agent removed.');
    } catch (error) {
      toast.error(error.message || 'Unable to remove judge agent');
    } finally {
      setMutationBusyId('');
    }
  }, [getAuthHeaders, judgeProfiles, matterId, reloadWorkspace, workspace]);

  const runMatterJudgeInference = useCallback(async () => {
    if (!matterId) return;
    setJudgeInferring(true);
    try {
      const response = await fetch(`${API}/cases/${matterId}/judges/infer`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Unable to infer judge context');
      setJudgeInferResults(payload.results || []);
      if (!(payload.results || []).length) {
        toast.success('No judge context surfaced from current matter files yet.');
      }
    } catch (error) {
      toast.error(error.message || 'Unable to infer judge context');
    } finally {
      setJudgeInferring(false);
    }
  }, [getAuthHeaders, matterId]);

  const toggleMiniJudgeStance = (stance) => {
    setMiniJudge((prev) => {
      const exists = prev.stance_tendencies.includes(stance);
      return {
        ...prev,
        stance_tendencies: exists
          ? prev.stance_tendencies.filter((item) => item !== stance)
          : [...prev.stance_tendencies, stance],
      };
    });
  };

  const setAcceptancePercent = (value) => {
    const acceptance = clampPercent(value, miniJudge.acceptance_percent);
    setMiniJudge((prev) => ({
      ...prev,
      acceptance_percent: acceptance,
      rejection_percent: 100 - acceptance,
    }));
  };

  const setRejectionPercent = (value) => {
    const rejection = clampPercent(value, miniJudge.rejection_percent);
    setMiniJudge((prev) => ({
      ...prev,
      rejection_percent: rejection,
      acceptance_percent: 100 - rejection,
    }));
  };

  const createMiniJudge = async () => {
    if (!miniJudge.name.trim()) {
      toast.error('Name the mini judge before adding it.');
      return;
    }
    await addJudgeToMatter({
      ...miniJudge,
      type: 'simulated',
      acceptance_percent: miniJudge.acceptance_percent,
      rejection_percent: miniJudge.rejection_percent,
      acceptance_tendency: Number((miniJudge.acceptance_percent / 100).toFixed(2)),
    }, 'mini-judge');
    setMiniJudge(buildMiniJudgeState());
  };

  const sendJudgeMessage = async () => {
    if (!matterId || !selectedJudge || !judgeChatInput.trim()) return;
    const nextUserMessage = judgeChatInput.trim();
    setJudgeChatInput('');
    setJudgeSubmitting(true);
    setJudgeThread((prev) => ({
      thread_id: visibleJudgeThread.thread_id || prev.thread_id || '',
      judge_id: selectedJudge.judge_id,
      messages: [...(visibleJudgeThread.messages || []), { role: 'user', content: nextUserMessage }],
    }));
    try {
      const response = await fetch(`${API}/cases/${matterId}/judge-chat`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          judge_id: selectedJudge.judge_id,
          message: nextUserMessage,
          thread_id: visibleJudgeThread.thread_id || undefined,
          workspace_id: workspace?.case?.workspace_id || undefined,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Judge chat failed');
      setJudgeThread({
        thread_id: payload.thread_id || '',
        judge_id: payload?.judge?.judge_id || selectedJudge.judge_id,
        messages: payload.messages || [],
      });
    } catch (error) {
      toast.error(error.message || 'Judge chat failed');
      setJudgeThread((prev) => ({
        thread_id: prev.thread_id || '',
        judge_id: selectedJudge.judge_id,
        messages: [...(prev.messages || []), { role: 'assistant', content: 'I could not complete that response cleanly. Please try again.' }],
      }));
    } finally {
      setJudgeSubmitting(false);
    }
  };

  return (
    <div data-testid="judge-strategy-workspace" style={{ display: 'grid', gap: '20px' }}>
      <section className="advoq-card" style={{ padding: '22px', borderRadius: '24px', display: 'grid', gap: '18px' }}>
        <AppSectionHeader
          eyebrow="Judge strategy"
          title="Bench prep inside this matter"
          description="Use judge strategy only when the file genuinely needs it. Search a real judge, infer context from this matter, or create a restrained simulation for rehearsal."
          actions={(
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Plan limit: {judgeQuotaText}</span>
              <button type="button" className="app-chat-button" onClick={runMatterJudgeInference} disabled={judgeInferring}>
                {judgeInferring ? <Loader2 size={13} className="animate-spin" /> : <FileStack size={13} />}
                Scan matter files
              </button>
            </div>
          )}
        />

        {judgeProfiles.length ? (
          <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
            {judgeProfiles.map((judge) => (
              <JudgeChip
                key={judge.judge_id}
                judge={judge}
                active={selectedJudge?.judge_id === judge.judge_id}
                disabled={mutationBusyId === judge.judge_id}
                onClick={() => setSelectedJudgeId(judge.judge_id)}
                onRemove={() => removeJudgeFromMatter(judge)}
              />
            ))}
          </div>
        ) : (
          <AppEmptyState
            icon={Gavel}
            title="No judge strategy attached yet"
            description="Bench prep stays optional. Add a judge only when it sharpens this matter instead of distracting from it."
          />
        )}

        <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
          <FlowCard
            title="Search a real judge"
            description="Pull from Indian Kanoon plus already-available verified Indian legal sources."
          >
            <input
              className="advoq-input"
              value={judgeSearchQuery}
              onChange={(event) => setJudgeSearchQuery(event.target.value)}
              placeholder="Search judge name"
            />
            {judgeSearching ? (
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Searching verified judge candidates…</div>
            ) : null}
            {(judgeSearchResults || []).slice(0, 4).map((result) => (
              <button
                key={result.judge_id}
                type="button"
                className="app-chat-button"
                onClick={() => addJudgeToMatter(result)}
                disabled={mutationBusyId === result.judge_id}
                style={{ justifyContent: 'space-between' }}
              >
                <span style={{ textAlign: 'left' }}>
                  <strong>{result.name}</strong>
                  <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 400 }}>
                    {result.verified_source_label}
                  </span>
                </span>
                <ArrowRight size={13} />
              </button>
            ))}
          </FlowCard>

          <FlowCard
            title="Infer from matter files"
            description="Surface judge context from uploaded documents and linked authorities already attached to this file."
          >
            {judgeInferResults.length ? judgeInferResults.slice(0, 4).map((result) => (
              <button
                key={result.judge_id}
                type="button"
                className="app-chat-button"
                onClick={() => addJudgeToMatter(result)}
                disabled={mutationBusyId === result.judge_id}
                style={{ justifyContent: 'space-between' }}
              >
                <span style={{ textAlign: 'left' }}>
                  <strong>{result.name}</strong>
                  <span style={{ display: 'block', color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 400 }}>
                    {result.verified_source_label || 'Matter-linked context'}
                  </span>
                </span>
                <ArrowRight size={13} />
              </button>
            )) : (
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                Use “Scan matter files” when the record includes an order header, cause list, or bench line.
              </div>
            )}
          </FlowCard>

          <FlowCard
            title="Create a mini judge agent"
            description="Useful for scenario planning, not prediction. Every custom judge stays clearly labelled as simulated."
          >
            <input
              className="advoq-input"
              value={miniJudge.name}
              onChange={(event) => setMiniJudge((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Judge name"
            />
            <input
              className="advoq-input"
              value={miniJudge.temperament}
              onChange={(event) => setMiniJudge((prev) => ({ ...prev, temperament: event.target.value }))}
              placeholder="Bench tone or stance"
            />
            <textarea
              className="advoq-input"
              rows={3}
              value={miniJudge.short_description}
              onChange={(event) => setMiniJudge((prev) => ({ ...prev, short_description: event.target.value }))}
              placeholder="Behavioral note for how this judge tends to move."
            />
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {STRATEGY_STANCES.map((stance) => {
                const active = miniJudge.stance_tendencies.includes(stance);
                return (
                  <button
                    key={stance}
                    type="button"
                    onClick={() => toggleMiniJudgeStance(stance)}
                    style={{
                      borderRadius: '999px',
                      border: active ? '1px solid rgba(15,118,110,0.38)' : '1px solid var(--border-default)',
                      background: active ? 'rgba(15,118,110,0.08)' : 'transparent',
                      color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                      padding: '7px 11px',
                      fontSize: '12px',
                      fontWeight: 700,
                    }}
                  >
                    {stance}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              <label style={{ display: 'grid', gap: '6px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Acceptance %</span>
                <input className="advoq-input" type="number" min="0" max="100" value={miniJudge.acceptance_percent} onChange={(event) => setAcceptancePercent(event.target.value)} />
              </label>
              <label style={{ display: 'grid', gap: '6px' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Rejection %</span>
                <input className="advoq-input" type="number" min="0" max="100" value={miniJudge.rejection_percent} onChange={(event) => setRejectionPercent(event.target.value)} />
              </label>
            </div>
            <button type="button" className="advoq-btn-primary" onClick={createMiniJudge} disabled={mutationBusyId === 'mini-judge'}>
              <Sparkles size={13} /> Add simulated judge
            </button>
          </FlowCard>
        </div>

        {selectedJudge ? (
          <div style={{ display: 'grid', gap: '14px', padding: '18px', borderRadius: '18px', border: '1px solid var(--border-default)', background: 'var(--bg-base)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ display: 'grid', gap: '8px' }}>
                <div style={{ color: 'var(--text-primary)', fontWeight: 800, fontSize: '18px' }}>
                  {selectedJudge.name}
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <SourceTrustBadge label={judgeTrustLabel(selectedJudge)} />
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                    {judgeTypeLabel(selectedJudge)}
                  </span>
                </div>
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '12px', textAlign: 'right' }}>
                {normalizeJudgeType(selectedJudge.type) === 'simulated'
                  ? `${selectedJudge.acceptance_percent || 50}% accept · ${selectedJudge.rejection_percent || 50}% reject`
                  : 'Strategic preparation with visible restraint'}
              </div>
            </div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.7 }}>
              {selectedJudge.short_description || selectedJudge.temperament || 'No bench note yet.'}
            </div>
            {(selectedJudge.provenance || []).length ? (
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {(selectedJudge.provenance || []).slice(0, 3).map((item, index) => (
                  <div
                    key={`${item.label || 'source'}-${index}`}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      borderRadius: '999px',
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-default)',
                      color: 'var(--text-secondary)',
                      fontSize: '12px',
                    }}
                  >
                    <span>{item.label || item.source || 'Source'}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {judgeAnalysisLoading ? (
              <div style={{ color: 'var(--text-secondary)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <Loader2 size={14} className="animate-spin" /> Building judge strategy read…
              </div>
            ) : judgeAnalysis ? (
              <div style={{ display: 'grid', gap: '12px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                <div style={{ gridColumn: '1 / -1', padding: '14px', borderRadius: '14px', background: 'rgba(15,118,110,0.08)', color: 'var(--text-primary)', lineHeight: 1.7 }}>
                  {judgeAnalysis.summary}
                </div>
                <AnalysisBlock label="Likely pressure points" items={judgeAnalysis.pressure_points || []} />
                <AnalysisBlock label="Likely objections" items={judgeAnalysis.likely_objections || []} />
                <AnalysisBlock label="Weak parts of the case" items={judgeAnalysis.weak_points || []} />
                <AnalysisBlock label="Hearing prep" items={judgeAnalysis.hearing_prep || []} />
                <AnalysisBlock label="Bench-style questions" items={judgeAnalysis.bench_questions || []} />
                <div style={{ gridColumn: '1 / -1', borderRadius: '14px', border: '1px solid var(--border-default)', padding: '14px', background: 'var(--bg-elevated)', display: 'grid', gap: '8px' }}>
                  <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>Acceptance and restraint</div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: 1.6 }}>
                    {judgeAnalysis.acceptance_dynamics}
                  </div>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.6 }}>
                    {judgeAnalysis.restraint_note}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="advoq-card" style={{ padding: '22px', borderRadius: '24px', display: 'grid', gap: '16px' }}>
        <AppSectionHeader
          eyebrow="Bench prep chat"
          title={selectedJudge ? `Pressure-test with ${selectedJudge.name}` : 'Bench prep chat'}
          description={selectedJudge
            ? (normalizeJudgeType(selectedJudge.type) === 'simulated'
              ? 'This stays clearly labelled as simulation for strategy rehearsal.'
              : 'This stays grounded in visible provenance and does not claim certainty about future bench conduct.')
            : 'Select a judge strategy profile to continue into bench-prep chat.'}
        />
        {selectedJudge ? (
          <>
            <div style={{ display: 'grid', gap: '10px', minHeight: '220px' }}>
              {(visibleJudgeThread.messages || []).length ? (visibleJudgeThread.messages || []).map((message, index) => (
                <div
                  key={`${message.role}-${index}`}
                  style={{
                    alignSelf: message.role === 'user' ? 'end' : 'start',
                    maxWidth: '84%',
                    padding: '12px 14px',
                    borderRadius: '18px',
                    background: message.role === 'user' ? 'rgba(15,118,110,0.12)' : 'var(--bg-base)',
                    border: '1px solid var(--border-default)',
                    color: 'var(--text-primary)',
                    lineHeight: 1.7,
                  }}
                >
                  {message.content}
                </div>
              )) : (
                <AppEmptyState icon={Bot} title="No bench-prep exchange yet" description="Ask for likely objections, weak answers, or the hardest bench question on this file." />
              )}
            </div>
            <div style={{ display: 'grid', gap: '10px' }}>
              <textarea className="advoq-input" rows={4} value={judgeChatInput} onChange={(event) => setJudgeChatInput(event.target.value)} placeholder="Ask what this judge would likely question, accept, reject, or push on next." />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <JollyDictationButton label="Jolly" onAppendText={(text) => setJudgeChatInput((prev) => `${prev}${prev ? ' ' : ''}${text}`)} />
                <button type="button" className="advoq-btn-primary" onClick={sendJudgeMessage} disabled={judgeSubmitting}>
                  {judgeSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
                  Send
                </button>
              </div>
            </div>
          </>
        ) : (
          <AppEmptyState icon={Gavel} title="Judge strategy is optional" description="Add a real or simulated judge only when it helps the matter move. Sabha synthesis stays inside the matter overview." />
        )}
      </section>
    </div>
  );
}
